/**
 * Piece Selection Heuristics for Sentinel AI
 *
 * Strategic piece selection based on synergies, counter-picks, and lane safety.
 * King selection remains random as per requirements.
 */

import { PieceType } from "../../types";

/**
 * Detailed reasoning for why a piece was selected
 */
export interface PieceSelectionReasoning {
  piece: PieceType;
  score: number;
  reasons: string[];
  breakdown: {
    counterBonus: number;
    synergyBonus: number;
    essentialSynergy: number;
    counterPenalty: number;
    laneSafety: number;
    antiSynergy: number;
    other: number;
  };
}

/**
 * Weights for scoring piece selection
 */
const WEIGHTS = {
  COUNTER_BONUS: 15,        // Bonus per piece this counters
  SYNERGY_BONUS: 20,        // Bonus for strong synergy
  COUNTER_PENALTY: 12,      // Penalty per piece that counters this
  LANE_SAFETY: 10,          // Lane safety consideration
  ESSENTIAL_SYNERGY: 100,   // Must-have combos (Monkey+Tree)
  ANTI_SYNERGY: -50,        // Terrible matchups (Octopus vs Tree/Fungi)
};

/**
 * Piece synergies: which pieces work well together
 */
const SYNERGIES: Record<PieceType, PieceType[]> = {
  [PieceType.Bear]: [PieceType.Wolf, PieceType.Crane, PieceType.Octopus],
  [PieceType.Crane]: [PieceType.Wolf, PieceType.Rhino],
  [PieceType.Frog]: [PieceType.Monkey, PieceType.Tree, PieceType.Fungi],
  [PieceType.Fungi]: [PieceType.Monkey],
  [PieceType.Goat]: [PieceType.Snake, PieceType.Crane, PieceType.Wolf],
  [PieceType.Monkey]: [PieceType.Tree, PieceType.Fungi],
  [PieceType.Octopus]: [PieceType.Bear, PieceType.Wolf, PieceType.Crane],
  [PieceType.Penguin]: [PieceType.Snake, PieceType.Wolf, PieceType.Virus],
  [PieceType.Rhino]: [PieceType.Snake, PieceType.Crane],
  [PieceType.Snake]: [PieceType.Virus, PieceType.Goat, PieceType.Penguin],
  [PieceType.Tree]: [PieceType.Monkey, PieceType.Frog],
  [PieceType.Virus]: [PieceType.Snake, PieceType.Penguin, PieceType.Crane],
  [PieceType.Wolf]: [PieceType.Bear, PieceType.Crane, PieceType.Goat, PieceType.Penguin, PieceType.Octopus],
};

/**
 * Essential synergies that provide massive bonuses
 */
const ESSENTIAL_SYNERGIES: Record<PieceType, PieceType[]> = {
  [PieceType.Monkey]: [PieceType.Tree],
  [PieceType.Tree]: [PieceType.Monkey],
  [PieceType.Bear]: [],
  [PieceType.Crane]: [],
  [PieceType.Frog]: [],
  [PieceType.Fungi]: [],
  [PieceType.Goat]: [],
  [PieceType.Octopus]: [],
  [PieceType.Penguin]: [],
  [PieceType.Rhino]: [],
  [PieceType.Snake]: [],
  [PieceType.Virus]: [],
  [PieceType.Wolf]: [],
};

/**
 * Counter relationships: which pieces counter which
 */
const COUNTERS: Record<PieceType, PieceType[]> = {
  [PieceType.Bear]: [PieceType.Wolf, PieceType.Rhino, PieceType.Monkey],
  [PieceType.Crane]: [PieceType.Penguin, PieceType.Snake, PieceType.Monkey, PieceType.Goat],
  [PieceType.Frog]: [PieceType.Wolf, PieceType.Crane],
  [PieceType.Fungi]: [PieceType.Tree, PieceType.Octopus],
  [PieceType.Goat]: [PieceType.Tree, PieceType.Wolf],
  [PieceType.Monkey]: [], // Special: counters Crane, Fungi, Octopus when WITH Tree
  [PieceType.Octopus]: [PieceType.Rhino, PieceType.Bear, PieceType.Wolf, PieceType.Crane],
  [PieceType.Penguin]: [], // Special: good against any team IF can kill early game
  [PieceType.Rhino]: [PieceType.Frog, PieceType.Monkey, PieceType.Tree, PieceType.Snake],
  [PieceType.Snake]: [PieceType.Frog],
  [PieceType.Tree]: [PieceType.Octopus], // Especially with Monkey
  [PieceType.Virus]: [PieceType.Bear, PieceType.Crane, PieceType.Frog, PieceType.Goat, PieceType.Monkey, PieceType.Octopus, PieceType.Penguin, PieceType.Rhino, PieceType.Snake, PieceType.Wolf], // Most animals
  [PieceType.Wolf]: [PieceType.Snake, PieceType.Rhino],
};

/**
 * Bad lane matchups (avoid these)
 */
const BAD_LANES: Record<PieceType, PieceType[]> = {
  [PieceType.Frog]: [PieceType.Snake, PieceType.Rhino], // VERY BAD
  [PieceType.Bear]: [],
  [PieceType.Crane]: [],
  [PieceType.Fungi]: [],
  [PieceType.Goat]: [],
  [PieceType.Monkey]: [],
  [PieceType.Octopus]: [],
  [PieceType.Penguin]: [],
  [PieceType.Rhino]: [],
  [PieceType.Snake]: [],
  [PieceType.Tree]: [],
  [PieceType.Virus]: [],
  [PieceType.Wolf]: [],
};

/**
 * Pieces that are weak against this piece
 */
const COUNTERED_BY: Record<PieceType, PieceType[]> = {
  [PieceType.Bear]: [PieceType.Octopus],
  [PieceType.Crane]: [PieceType.Frog, PieceType.Octopus],
  [PieceType.Frog]: [PieceType.Rhino, PieceType.Snake],
  [PieceType.Fungi]: [PieceType.Monkey], // When Monkey is with Tree
  [PieceType.Goat]: [PieceType.Crane],
  [PieceType.Monkey]: [PieceType.Bear, PieceType.Crane, PieceType.Rhino],
  [PieceType.Octopus]: [PieceType.Fungi, PieceType.Tree],
  [PieceType.Penguin]: [PieceType.Crane],
  [PieceType.Rhino]: [PieceType.Bear, PieceType.Octopus, PieceType.Wolf],
  [PieceType.Snake]: [PieceType.Crane, PieceType.Rhino, PieceType.Wolf],
  [PieceType.Tree]: [PieceType.Fungi, PieceType.Goat, PieceType.Rhino],
  [PieceType.Virus]: [PieceType.Tree, PieceType.Fungi], // Bad against non-animals
  [PieceType.Wolf]: [PieceType.Bear, PieceType.Frog, PieceType.Goat, PieceType.Octopus],
};

/**
 * Select a piece strategically based on heuristics
 * Returns both the piece and detailed reasoning
 */
export function selectPieceStrategicWithReasoning(
  availablePieces: PieceType[],
  opponentPieces: PieceType[],
  ownPieces: PieceType[]
): PieceSelectionReasoning {
  // Score each available piece
  const allReasonings = availablePieces.map(piece =>
    calculatePieceScoreWithReasoning(piece, opponentPieces, ownPieces)
  );

  // Sort by score (highest first)
  allReasonings.sort((a, b) => b.score - a.score);

  // Return highest scoring piece with reasoning
  return allReasonings[0];
}

/**
 * Legacy function for backward compatibility - returns just the piece
 */
export function selectPieceStrategic(
  availablePieces: PieceType[],
  opponentPieces: PieceType[],
  ownPieces: PieceType[]
): PieceType {
  return selectPieceStrategicWithReasoning(availablePieces, opponentPieces, ownPieces).piece;
}

/**
 * Calculate a score for a piece with detailed reasoning
 */
function calculatePieceScoreWithReasoning(
  piece: PieceType,
  opponentPieces: PieceType[],
  ownPieces: PieceType[]
): PieceSelectionReasoning {
  let score = 0;
  const reasons: string[] = [];
  const breakdown = {
    counterBonus: 0,
    synergyBonus: 0,
    essentialSynergy: 0,
    counterPenalty: 0,
    laneSafety: 0,
    antiSynergy: 0,
    other: 0,
  };

  // 1. COUNTER BONUS: Does this piece counter opponent pieces?
  const counteredPieces = COUNTERS[piece].filter(counter =>
    opponentPieces.includes(counter)
  );
  const counterBonus = counteredPieces.length * WEIGHTS.COUNTER_BONUS;
  breakdown.counterBonus = counterBonus;
  score += counterBonus;
  if (counteredPieces.length > 0) {
    reasons.push(`Counters opponent's ${counteredPieces.join(', ')} (+${counterBonus})`);
  }

  // 2. SYNERGY BONUS: Does this piece synergize with our pieces?
  const synergizedPieces = SYNERGIES[piece].filter(synergy =>
    ownPieces.includes(synergy)
  );
  const synergyBonus = synergizedPieces.length * WEIGHTS.SYNERGY_BONUS;
  breakdown.synergyBonus = synergyBonus;
  score += synergyBonus;
  if (synergizedPieces.length > 0) {
    reasons.push(`Synergizes with ${synergizedPieces.join(', ')} (+${synergyBonus})`);
  }

  // 3. ESSENTIAL SYNERGY: Massive bonus for critical combos
  const essentialPieces = ESSENTIAL_SYNERGIES[piece].filter(synergy =>
    ownPieces.includes(synergy)
  );
  const essentialBonus = essentialPieces.length * WEIGHTS.ESSENTIAL_SYNERGY;
  breakdown.essentialSynergy = essentialBonus;
  score += essentialBonus;
  if (essentialPieces.length > 0) {
    reasons.push(`🔥 ESSENTIAL combo with ${essentialPieces.join(', ')}! (+${essentialBonus})`);
  }

  // Special case: Monkey counters Crane, Fungi, Octopus when WITH Tree
  if (piece === PieceType.Monkey && ownPieces.includes(PieceType.Tree)) {
    const monkeyCounteredPieces = [PieceType.Crane, PieceType.Fungi, PieceType.Octopus].filter(counter =>
      opponentPieces.includes(counter)
    );
    const monkeyBonus = monkeyCounteredPieces.length * WEIGHTS.COUNTER_BONUS;
    breakdown.counterBonus += monkeyBonus;
    score += monkeyBonus;
    if (monkeyCounteredPieces.length > 0) {
      reasons.push(`Monkey+Tree combo counters ${monkeyCounteredPieces.join(', ')} (+${monkeyBonus})`);
    }
  }

  // 4. COUNTER PENALTY: Is this piece countered by opponent pieces?
  const weakAgainst = COUNTERED_BY[piece].filter(counter =>
    opponentPieces.includes(counter)
  );
  const counterPenalty = weakAgainst.length * WEIGHTS.COUNTER_PENALTY;
  breakdown.counterPenalty = -counterPenalty;
  score -= counterPenalty;
  if (weakAgainst.length > 0) {
    reasons.push(`⚠️ Weak against opponent's ${weakAgainst.join(', ')} (-${counterPenalty})`);
  }

  // 5. LANE SAFETY: Avoid bad lane matchups
  const badMatches = BAD_LANES[piece].filter(badMatch =>
    opponentPieces.includes(badMatch)
  );
  const lanePenalty = badMatches.length * WEIGHTS.LANE_SAFETY;
  breakdown.laneSafety = -lanePenalty;
  score -= lanePenalty;
  if (badMatches.length > 0) {
    reasons.push(`🚫 BAD lane vs ${badMatches.join(', ')} (-${lanePenalty})`);
  }

  // 6. ANTI-SYNERGY: Specific bad matchups
  // Octopus vs Tree/Fungi
  if (piece === PieceType.Octopus && (opponentPieces.includes(PieceType.Tree) || opponentPieces.includes(PieceType.Fungi))) {
    breakdown.antiSynergy = WEIGHTS.ANTI_SYNERGY;
    score += WEIGHTS.ANTI_SYNERGY;
    reasons.push(`Poor matchup vs Tree/Fungi (${WEIGHTS.ANTI_SYNERGY})`);
  }

  // Virus vs Tree/Fungi (non-animals)
  if (piece === PieceType.Virus) {
    if (opponentPieces.includes(PieceType.Tree) || opponentPieces.includes(PieceType.Fungi)) {
      breakdown.antiSynergy = WEIGHTS.ANTI_SYNERGY;
      score += WEIGHTS.ANTI_SYNERGY;
      reasons.push(`Can't corrupt Tree/Fungi (${WEIGHTS.ANTI_SYNERGY})`);
    }
    // Bonus for having lots of animals to corrupt
    const animalCount = opponentPieces.filter(p => p !== PieceType.Tree && p !== PieceType.Fungi).length;
    const virusBonus = animalCount * 10;
    breakdown.other += virusBonus;
    score += virusBonus;
    if (animalCount > 0) {
      reasons.push(`Can corrupt ${animalCount} animals (+${virusBonus})`);
    }
  }

  // Penguin is usually weak unless early game trading opportunity
  if (piece === PieceType.Penguin) {
    breakdown.other -= 15;
    score -= 15;
    reasons.push(`Generally weak pick (-15)`);
  }

  // Wolf is a reliable all-rounder - small bonus
  if (piece === PieceType.Wolf) {
    breakdown.other += 5;
    score += 5;
    reasons.push(`Reliable all-rounder (+5)`);
  }

  return {
    piece,
    score,
    reasons,
    breakdown,
  };
}

/**
 * Select a king randomly (as per requirements)
 */
export function selectKingRandom(availableKings: PieceType[]): PieceType {
  // Filter out Virus (cannot be king)
  const validKings = availableKings.filter(k => k !== PieceType.Virus);

  if (validKings.length === 0) {
    throw new Error('No valid kings available for selection');
  }

  const randomIndex = Math.floor(Math.random() * validKings.length);
  return validKings[randomIndex];
}
